import { BookOpenIcon } from '@heroicons/react/24/solid';
import { ChangeEvent, ElementType, lazy, MouseEvent, Suspense, useState } from 'react';

import { Button, ButtonProps } from './Button';
import { BibleIcon } from '../icons/BibleIcon';
import { PaletteIcon } from '../icons/PaletteIcon';
import { SearchBox } from '../../search/components/SearchBox';
import { useLibraryContext } from '../../library/context';

const Library = lazy(() => import('../../library/components/Library').then(module => ({ default: module.Library })));
const ThemeEditorPane = lazy(() => import('../../themes/components/ThemeEditorPane').then(module => ({ default: module.ThemeEditorPane })));
const BiblePane = lazy(() => import('../../bible/components/BiblePane').then(module => ({ default: module.BiblePane })));

type Tool = 'library' | 'bible' | 'themes' | null;

type ToolButtonProps = Omit<ButtonProps, 'children'> & {
  icon: ElementType,
  name: string,
  onClick: (event: MouseEvent) => void,
}

export const ToolButton = ({ active, icon, name, onClick }: ToolButtonProps) => {
  const Icon = icon;
  return (
    <Button
      active={active}
      onClick={onClick}
    >
      <Icon className='size-6 inline-block mx-1' />
      { name }
    </Button>
  );
};

export const Tools = () => {

  const [activeTool, setActiveTool] = useState<Tool>(null);

  const changeTool = (tool: Tool) => {
    if (tool === activeTool) {
      setActiveTool(null);
    }
    else {
      setActiveTool(tool);
    }
  };

  const libraryContext = useLibraryContext();

  const close = () => {
    setActiveTool(null);
    libraryContext.setFocusedIndex(-1);
    libraryContext.setSearchQuery('');
  };

  const handleSearchInput = (evt: ChangeEvent<HTMLInputElement>) => {
    const query = evt.target.value;
    libraryContext.setSearchQuery(query);
    libraryContext.setFocusedIndex(-1);
    setActiveTool('library');
  };

  const openLibrary = () => {
    changeTool('library');
    libraryContext.searchboxRef.current?.focus();
  };

  return (
    <>
      <div className='flex mb-2 z-30'>
        <ToolButton
          active={activeTool === 'library'}
          icon={BookOpenIcon}
          name='Library'
          onClick={openLibrary}
        />
        <ToolButton
          active={activeTool === 'bible'}
          icon={BibleIcon}
          name='Bible'
          onClick={() => changeTool('bible')}
        />
        <ToolButton
          active={activeTool === 'themes'}
          icon={PaletteIcon}
          name='Themes'
          onClick={() => changeTool('themes')}
        />
      </div>
      <div className='mb-2 text-neutral-800'>
        <SearchBox
          close={close}
          onInput={handleSearchInput}
        />
      </div>
      <Suspense>
        <Library
          close={close}
          shown={activeTool === 'library'}
        />
      </Suspense>
      <Suspense>
        <BiblePane
          close={close}
          shown={activeTool === 'bible'}
        />
      </Suspense>
      <Suspense>
        <ThemeEditorPane
          close={close}
          shown={activeTool === 'themes'}
        />
      </Suspense>
    </>
  );
};
